import { useEffect, useRef, useCallback } from "react";

const Draggable = ({
  children,
  id,
  style,
  draggable,
  headerRef,
  changePosition,
  onClick = () => {},
  onFinishDrag = () => {},
}) => {
  const left = useRef(0);
  const top = useRef(0);
  const _window = useRef();

  const dragElement = useCallback((e) => {
    const ele = _window.current;
    e = e || window.event;
    e.preventDefault();
    const currentX = Math.max(0, Math.min(e.clientX, window.innerWidth));
    const currentY = Math.max(0, Math.min(e.clientY, window.innerHeight));
    const diffLeft = left.current - currentX;
    const diffTop = top.current - currentY;
    left.current = currentX;
    top.current = currentY;
    ele.style.top =
      Math.max(0, Math.min(ele.offsetTop - diffTop, window.innerHeight - 25)) +
      "px";
    ele.style.left =
      Math.max(
        0,
        Math.min(ele.offsetLeft - diffLeft, window.innerWidth - 120)
      ) + "px";
  }, []);

  const touchDragElement = useCallback((e) => {
    const ele = _window.current;
    e = e || window.event;
    const currentX = Math.max(
      0,
      Math.min(e.touches[0].clientX, window.innerWidth)
    );
    const currentY = Math.max(
      0,
      Math.min(e.touches[0].clientY, window.innerHeight)
    );
    const diffLeft = left.current - currentX;
    const diffTop = top.current - currentY;
    left.current = currentX;
    top.current = currentY;
    ele.style.top =
      Math.max(0, Math.min(ele.offsetTop - diffTop, window.innerHeight - 25)) +
      "px";
    ele.style.left =
      Math.max(
        0,
        Math.min(ele.offsetLeft - diffLeft, window.innerWidth - 120)
      ) + "px";
  }, []);

  const closeDragElement = useCallback((e) => {
    const ele = _window.current;
    headerRef.current.style.cursor = "grab";
    _window.current.style.transition = "top 0.1s, left 0.1s";
    changePosition(ele.style.left, ele.style.top);
    window.removeEventListener("mouseup", closeDragElement);
    window.removeEventListener("mousemove", dragElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "auto";
    }
    onFinishDrag(ele.style.left, ele.style.top);
  }, []);

  const closeTouchDragElement = useCallback((e) => {
    const ele = _window.current;
    headerRef.current.style.cursor = "grab";
    _window.current.style.transition = "top 0.1s, left 0.1s";
    changePosition(ele.style.left, ele.style.top);
    window.removeEventListener("touchend", closeTouchDragElement);
    window.removeEventListener("touchmove", touchDragElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "auto";
    }
    onFinishDrag(ele.style.left, ele.style.top);
  }, []);

  const dragMouseDown = useCallback((e) => {
    e = e || window.event;
    e.preventDefault();
    left.current = e.clientX;
    top.current = e.clientY;
    headerRef.current.style.cursor = "grabbing";
    _window.current.style.transition = "none";
    window.addEventListener("mouseup", closeDragElement);
    window.addEventListener("mousemove", dragElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "none";
    }
  }, []);

  const dragTouchStart = useCallback((e) => {
    e = e || window.event;
    e.preventDefault();
    left.current = e.touches[0].clientX;
    top.current = e.touches[0].clientY;
    headerRef.current.style.cursor = "grabbing";
    _window.current.style.transition = "none";
    window.addEventListener("touchend", closeTouchDragElement);
    window.addEventListener("touchmove", touchDragElement);
    const iframes = document.getElementsByTagName("iframe");
    for (let f of iframes) {
      f.style.pointerEvents = "none";
    }
  }, []);

  useEffect(() => {
    if (draggable) {
      if (headerRef.current) {
        headerRef.current.addEventListener("mousedown", dragMouseDown);
        headerRef.current.addEventListener("touchstart", dragTouchStart);
      }
    } else {
      headerRef.current.removeEventListener("mousedown", dragMouseDown);
      headerRef.current.removeEventListener("touchstart", dragTouchStart);
      window.removeEventListener("mouseup", closeDragElement);
      window.removeEventListener("mousemove", dragElement);
      window.removeEventListener("touchend", closeTouchDragElement);
      window.removeEventListener("touchmove", touchDragElement);
      _window.current.style.top = 0;
      _window.current.style.left = 0;
    }
    return () => {
      if (headerRef.current) {
        headerRef.current.removeEventListener("mousedown", dragMouseDown);
        headerRef.current.removeEventListener("touchstart", dragTouchStart);
      }
    };
  }, [draggable]);

  return (
    <div
      id={id + "_window"}
      ref={(ref) => {
        _window.current = ref;
      }}
      style={style}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export default Draggable;
